import { motion } from 'framer-motion';
import { MainLayout } from 'components/Layout';
import { useEffect } from 'react';

const variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.6,
      ease: [0.23, 1, 0.32, 1],
    },
  }),
};

export const EmergingTechnologies = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MainLayout
      title="Emerging Technologies in Africa"
      description="A deep dive into the technologies reshaping Africa’s digital future."
    >
      <div className="mx-auto max-w-3xl pb-20">
        {/* HEADER */}
        <motion.header initial="hidden" animate="visible" className="mb-12 space-y-4">
          <motion.p
            custom={1}
            variants={variants}
            className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400"
          >
            Insights • Technology • Africa
          </motion.p>

          <motion.h1
            custom={2}
            variants={variants}
            className="text-balance text-4xl font-semibold text-slate-50 sm:text-5xl"
          >
            Emerging Technologies Powering Africa’s Next Digital Leap
          </motion.h1>

          <motion.p custom={3} variants={variants} className="max-w-2xl text-base text-slate-400">
            Across fintech, AI, renewable energy, and digital infrastructure, African innovators are
            building solutions that not only transform industries, but redefine what progress looks
            like on the continent. This is a breakdown of the key technologies shaping Africa’s next
            decade of growth.
          </motion.p>
        </motion.header>

        {/* HERO IMAGE */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
          className="relative mb-14 overflow-hidden rounded-3xl border border-slate-800/70 bg-slate-900/50 shadow-xl"
        >
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-tr from-cyan-500/10 via-purple-600/10 to-fuchsia-500/10 mix-blend-overlay"
          />
          <img
            src="https://images.unsplash.com/photo-1558021211-6d1403321394?auto=format&fit=crop&q=80&w=1400"
            alt="Africa Tech"
            className="h-[360px] w-full object-cover opacity-80"
          />
        </motion.div>

        {/* TABLE OF CONTENTS */}
        <motion.nav
          initial="hidden"
          animate="visible"
          className="mb-12 rounded-2xl border border-slate-800/70 bg-slate-900/40 p-5 text-sm backdrop-blur"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            In this article
          </p>
          <ul className="space-y-2 text-slate-300">
            <li>
              <a href="#fintech" className="hover:text-cyan-400">
                Fintech & Mobile Money
              </a>
            </li>
            <li>
              <a href="#ai" className="hover:text-cyan-400">
                AI & Data Innovation
              </a>
            </li>
            <li>
              <a href="#infrastructure" className="hover:text-cyan-400">
                Digital Infrastructure
              </a>
            </li>
            <li>
              <a href="#renewable" className="hover:text-cyan-400">
                Renewable Energy Tech
              </a>
            </li>
            <li>
              <a href="#agritech" className="hover:text-cyan-400">
                Agritech & Food Systems
              </a>
            </li>
            <li>
              <a href="#future" className="hover:text-cyan-400">
                What the Future Holds
              </a>
            </li>
          </ul>
        </motion.nav>

        {/* CONTENT SECTIONS */}

        {/* — Fintech — */}
        <Section id="fintech" title="Fintech & Mobile Money">
          <p>
            Africa remains a global leader in <strong>mobile money adoption</strong>, with platforms
            like M-Pesa, MoMo, Airtel Money, Chipper Cash, and Flutterwave enabling seamless digital
            transactions for millions. These tools are not just conveniences—they are essential
            infrastructure.
          </p>

          <Highlight>
            As of 2024, over <strong>70% of global mobile money transactions</strong> originate from
            Africa.
          </Highlight>

          <p>
            Adoption is accelerating in cross-border payments, merchant tools, remittances,
            decentralized finance, micro-lending, and blockchain-powered financial rails, making
            fintech one of the most transformative sectors on the continent.
          </p>
        </Section>

        {/* — AI — */}
        <Section id="ai" title="AI & Data Innovation">
          <p>
            African AI ecosystems are maturing fast. From language models trained on Swahili,
            Yoruba, and Luganda, to AI-powered health diagnostics and smart logistics, local
            innovators are building AI solutions grounded in the continent’s unique needs.
          </p>

          <Quote>“AI will not replace African jobs. African entrepreneurs using AI will.”</Quote>

          <p>
            Governments and startups are increasingly investing in{' '}
            <strong>data centers, GPU clusters, and AI research hubs</strong>. Countries like Kenya,
            Rwanda, South Africa, and Nigeria are now considered emerging AI hotspots.
          </p>
        </Section>

        {/* — Infrastructure — */}
        <Section id="infrastructure" title="Digital Infrastructure">
          <p>
            Undersea cables, low-orbit satellite internet, data centers, and fiber networks are
            expanding rapidly across the continent. This infrastructure is unlocking opportunities
            for:
          </p>

          <ul className="list-disc space-y-2 pl-5 text-slate-400">
            <li>Cloud adoption for startups and enterprises</li>
            <li>Remote work and global collaboration</li>
            <li>Modern fintech, healthtech, and e-commerce</li>
            <li>Next-gen developer ecosystems</li>
          </ul>

          <p>
            Initiatives like Starlink, Google’s Equiano cable, and AWS & Azure expansions are
            playing a massive role in reducing latency and improving affordability.
          </p>
        </Section>

        {/* — Renewable — */}
        <Section id="renewable" title="Renewable Energy Technology">
          <p>
            Africa is becoming a testbed for innovative solar, battery, and mini-grid solutions.
            With over <strong>600 million people still lacking reliable electricity</strong>,
            distributed renewable systems are essential.
          </p>

          <p>
            Solar-powered ATMs, battery-backed mobile towers, smart metering, and pay-as-you-go
            solar home systems are transforming access to energy at scale.
          </p>
        </Section>

        {/* — Agritech — */}
        <Section id="agritech" title="Agritech & Food Systems">
          <p>
            Drones, AI-based crop analytics, smart irrigation, and digital farmer platforms are
            reshaping Africa’s agricultural backbone. Startups like Twiga, Apollo Agriculture, and
            Hello Tractor are enabling smallholder farmers to increase yields and access new
            markets.
          </p>
        </Section>

        {/* — Future — */}
        <Section id="future" title="What the Future Holds">
          <p>
            Africa sits at a unique inflection point: a young population, rapidly expanding internet
            penetration, and increasingly sophisticated startup ecosystems. The next decade will be
            defined by:
          </p>

          <ul className="list-disc space-y-2 pl-5 text-slate-400">
            <li>AI-powered local industries</li>
            <li>Blockchain-enabled financial systems</li>
            <li>Smart cities & IoT infrastructure</li>
            <li>Decentralized energy networks</li>
            <li>Homegrown deep-tech innovations</li>
          </ul>

          <p>
            The world is watching—but more importantly, Africa is building. And this wave of
            emerging technologies is only the beginning.
          </p>
          <div className="mt-8 text-xs">Author: Jonathan Mwebaze</div>
        </Section>
      </div>
    </MainLayout>
  );
};

/* -------------------------------- *
 *  Smaller Reusable Components
 * -------------------------------- */

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-14 scroll-mt-20">
    <motion.h2
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="mb-5 text-2xl font-semibold text-slate-50"
    >
      {title}
    </motion.h2>

    <div className="space-y-4 text-base leading-relaxed text-slate-300">{children}</div>
  </section>
);

const Quote = ({ children }) => (
  <blockquote className="my-6 border-l-4 border-cyan-400/40 pl-4 italic text-slate-400">
    {children}
  </blockquote>
);

const Highlight = ({ children }) => (
  <div className="my-5 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4 text-slate-300 shadow-sm backdrop-blur">
    {children}
  </div>
);
